import { jsx } from "react/jsx-runtime";
import { useEffect, useRef, useState, useCallback } from "react";
import { useTranslations } from "@vocab/react";
import { Autosuggest, IconLocation } from "braid-design-system";
import { v4 } from "uuid";
import { translations } from "./.vocab/index.mjs";
import { useGetJobLocationsQuery, useTrackLocationSuggestionMutation } from "./graphql-types.chunk.mjs";
import { getReverseHighlight } from "@seek/reverse-highlight";
const deepCompareEquals = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const useDeepCompareMemoize = (value) => {
  const ref = useRef();
  if (!deepCompareEquals(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
};
const useDeepCompareEffect = (callback, dependencies) => {
  useEffect(callback, dependencies.map(useDeepCompareMemoize));
};
const useDebounce = (value, delayInMs) => {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useDeepCompareEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delayInMs);
    return () => {
      clearTimeout(handler);
    };
  }, [value, delayInMs]);
  return debouncedValue;
};
const useLocationsQuery = ({
  advertiserId,
  sessionId,
  queryText,
  context,
  countries,
  excludeLocations,
  language
}) => {
  const { data, error, loading } = useGetJobLocationsQuery({
    variables: {
      advertiserId,
      sessionId,
      queryText,
      context,
      countries,
      excludeLocations,
      language
    },
    skip: !Boolean(queryText),
    errorPolicy: "all"
  });
  const suggestions = (data == null ? void 0 : data.locations) || [];
  return {
    loading,
    error,
    suggestions
  };
};
const TYPE_SEQUENCE = [
  "Country",
  "State",
  "Region",
  "SEEKLocation",
  "SEEKArea",
  "Suburb"
];
const makeSuggestions = ({
  suggestions,
  location: inputLocation,
  groupLabel
}) => {
  const mappedSuggestion = suggestions.map((suggestion) => ({
    text: suggestion.text,
    value: suggestion.value,
    type: suggestion.type,
    countryCode: suggestion.countryCode,
    highlights: getReverseHighlight(suggestion.text, inputLocation.text)
  }));
  if (!groupLabel) {
    return mappedSuggestion;
  }
  const uniqueTypes = /* @__PURE__ */ new Set();
  for (const suggestion of mappedSuggestion) {
    uniqueTypes.add(suggestion.type);
  }
  const filteredSequence = TYPE_SEQUENCE.filter(
    (type) => uniqueTypes.has(type)
  );
  return mappedSuggestion.reduce((groups, suggestion) => {
    const existingGroup = groups.find(
      (group) => group.type === suggestion.type
    );
    if (existingGroup) {
      existingGroup.suggestions.push(suggestion);
    } else {
      const newIndex = filteredSequence.indexOf(suggestion.type);
      const newGroup = {
        label: groupLabel[suggestion.type],
        type: suggestion.type,
        suggestions: [suggestion]
      };
      groups.splice(newIndex, 0, newGroup);
    }
    return groups;
  }, []);
};
const validateInternal = (location, suggestions, error, noMatchesMessage) => {
  const { text } = location;
  if (Boolean(text)) {
    if (error) {
      return { message: error.message, isValid: false };
    }
    if (suggestions.length === 0) {
      return { message: noMatchesMessage, isValid: false };
    }
  }
  return { message: "", isValid: true };
};
const validateComponent = (location, requiredMessage) => {
  const { text, value } = location;
  if (!Boolean(text) || !Boolean(value)) {
    return { message: requiredMessage, isValid: false };
  }
  return { message: "", isValid: true };
};
const LocationAutoSuggest = ({
  advertiserId,
  description,
  id,
  input,
  isValidationRequested,
  label,
  language,
  onBlur,
  onChange,
  onClear,
  onFocus,
  secondaryLabel,
  context,
  countries,
  clearAfterSelection,
  placeholder,
  excludeLocationsFromSearchResult,
  scrollToTopOnMobile = true,
  groupByType
}) => {
  const { t } = useTranslations(translations);
  const [sessionId, setSessionId] = useState(v4());
  const [location, setLocation] = useState(input);
  const debouncedState = useDebounce(
    {
      sessionId,
      locationText: location.text
    },
    250
  );
  const { loading, error, suggestions } = useLocationsQuery({
    advertiserId,
    sessionId: debouncedState.sessionId,
    queryText: debouncedState.locationText,
    context,
    countries,
    excludeLocations: excludeLocationsFromSearchResult,
    language
  });
  const [trackLocationSuggestionMutation] = useTrackLocationSuggestionMutation();
  const locationRequiredMessage = t("job-location-validation-required");
  const componentValidation = isValidationRequested ? validateComponent(location, locationRequiredMessage) : { isValid: true, message: "" };
  const [isValid, setIsValid] = useState(componentValidation.isValid);
  const [message, setMessage] = useState(componentValidation.message);
  const componentIsValid = loading ? loading : isValid;
  const componentMessage = loading ? "" : message;
  useEffect(() => {
    if (!(location && location.text)) {
      setLocation(input);
    }
  }, [input]);
  useEffect(() => {
    if (location.value) {
      onChange({
        isValid: componentIsValid,
        location
      });
    }
  }, [componentIsValid, debouncedState.locationText, location, onChange]);
  const validate = useCallback(() => {
    const { isValid: isValidLocation, message: messageToSet } = validateComponent(location, locationRequiredMessage);
    setMessage(messageToSet);
    setIsValid(isValidLocation);
  }, [location, locationRequiredMessage]);
  useEffect(() => {
    if (isValidationRequested) {
      validate();
    }
  }, [isValidationRequested, validate]);
  const onClearInternal = useCallback(() => {
    if (onClear) {
      setLocation({
        text: ""
      });
      onClear();
    }
  }, [onClear]);
  const isOnLocationSelect = (selectedLocation) => Boolean(selectedLocation.value);
  function onChangeInternal(selectedLocation) {
    const { isValid: isValidLocation, message: messageToSet } = validateInternal(
      selectedLocation,
      suggestions,
      error,
      t("job-location-validation-no-matches")
    );
    setMessage(messageToSet);
    setIsValid(isValidLocation);
    const suggestion = suggestions.find(
      (s) => s.value === selectedLocation.value
    );
    selectedLocation.countryCode = suggestion ? suggestion.countryCode : "";
    if (clearAfterSelection && isOnLocationSelect(selectedLocation)) {
      setLocation({ text: "" });
      onChange({
        isValid: componentIsValid,
        location: selectedLocation
      });
    } else {
      setLocation(selectedLocation);
    }
    if (isOnLocationSelect(selectedLocation) && isValidLocation) {
      trackLocationSuggestionMutation({
        variables: {
          sessionId,
          locationId: selectedLocation.value
        }
      });
      setSessionId(v4());
    }
  }
  return /* @__PURE__ */ jsx(
    Autosuggest,
    {
      id,
      icon: /* @__PURE__ */ jsx(IconLocation, {}),
      label,
      secondaryLabel,
      description,
      onBlur,
      onChange: onChangeInternal,
      onClear: onClear ? onClearInternal : void 0,
      onFocus,
      suggestions: makeSuggestions({
        suggestions,
        location,
        groupLabel: groupByType && {
          Suburb: t("job-location-suggestion-suburb-group-label"),
          SEEKArea: t("job-location-suggestion-area-group-label"),
          SEEKLocation: t("job-location-suggestion-location-group-label"),
          Region: t("job-location-suggestion-region-group-label"),
          State: t("job-location-suggestion-state-group-label"),
          Country: t("job-location-suggestion-country-group-label")
        }
      }),
      value: location,
      tone: componentIsValid ? "neutral" : "critical",
      message: componentMessage,
      placeholder,
      scrollToTopOnMobile,
      reserveMessageSpace: true,
      automaticSelection: true
    }
  );
};
const JobLocation = ({
  id,
  description,
  label,
  secondaryLabel,
  onBlur,
  onChange,
  onClear,
  onFocus,
  advertiserId = "",
  isValidationRequested = false,
  value = { text: "" },
  language,
  context,
  countries,
  clearAfterSelection = false,
  excludeLocationsFromSearchResult,
  placeholder,
  scrollToTopOnMobile,
  groupByType
}) => {
  const handleChange = useCallback(
    ({ isValid, location }) => {
      if (onChange) {
        onChange({
          isValid,
          location
        });
      }
    },
    [onChange]
  );
  return /* @__PURE__ */ jsx(
    LocationAutoSuggest,
    {
      advertiserId,
      description,
      id,
      input: value,
      language,
      isValidationRequested,
      label,
      onBlur,
      onChange: handleChange,
      onClear,
      onFocus,
      secondaryLabel,
      context,
      countries,
      clearAfterSelection,
      placeholder,
      excludeLocationsFromSearchResult,
      scrollToTopOnMobile,
      groupByType
    }
  );
};
export {
  JobLocation
};
